/**
 * Table of Contents Generator Component
 * Provides UI for generating and customizing professional table of contents
 */

import React, { useState, useEffect } from 'react';
import { ProjectState } from '../types/project';
import {
  generateEnhancedTableOfContents,
  applyTOCToProject,
  formatTOC,
  TOC_STYLES,
  TOCStyle,
  EnhancedTableOfContents,
  TOCEntry
} from '../utils/smartTableOfContents';

interface TableOfContentsGeneratorProps {
  projectState: ProjectState;
  onTOCApply: (updatedProject: Partial<ProjectState>) => void;
  isOpen: boolean;
  onClose: () => void;
}

const TableOfContentsGenerator: React.FC<TableOfContentsGeneratorProps> = ({
  projectState,
  onTOCApply,
  isOpen,
  onClose
}) => {
  const [enhancedTOC, setEnhancedTOC] = useState<EnhancedTableOfContents | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string>('standard');
  const [previewTOC, setPreviewTOC] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Generate TOC when component mounts or project state changes
  useEffect(() => {
    if (isOpen && projectState) {
      generateTOC();
    }
  }, [isOpen, projectState]);

  // Update preview when style changes
  useEffect(() => {
    if (enhancedTOC) {
      const style = TOC_STYLES.find(s => s.id === selectedStyle) || TOC_STYLES[0];
      setPreviewTOC(formatTOC(enhancedTOC.entries, style));
    }
  }, [enhancedTOC, selectedStyle]);

  const generateTOC = async () => {
    setIsGenerating(true);
    try {
      // Simulate processing time for large books
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const toc = generateEnhancedTableOfContents(projectState);
      setEnhancedTOC(toc);
      
      const style = TOC_STYLES.find(s => s.id === selectedStyle) || TOC_STYLES[0];
      setPreviewTOC(formatTOC(toc.entries, style));
    } catch (error) {
      console.error('Error generating TOC:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApplyTOC = () => {
    if (enhancedTOC) {
      const updatedProject = applyTOCToProject(projectState, enhancedTOC, selectedStyle);
      onTOCApply(updatedProject);
      onClose();
    }
  };

  const handleStyleChange = (styleId: string) => {
    setSelectedStyle(styleId);
  };

  const renderTOCStatistics = () => {
    if (!enhancedTOC) return null;

    const { metadata } = enhancedTOC;
    return (
      <div className="bg-gray-50 p-4 rounded-lg mb-4">
        <h4 className="font-semibold text-gray-800 mb-2">Table of Contents Statistics</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Total Entries:</span>
            <span className="font-medium ml-2">{metadata.totalEntries}</span>
          </div>
          <div>
            <span className="text-gray-600">Est. Pages:</span>
            <span className="font-medium ml-2">{metadata.estimatedReadingTime}</span>
          </div>
          <div>
            <span className="text-gray-600">Avg Chapter Length:</span>
            <span className="font-medium ml-2">{metadata.averageChapterLength.toLocaleString()} words</span>
          </div>
          <div>
            <span className="text-gray-600">Est. Reading Time:</span>
            <span className="font-medium ml-2">{metadata.estimatedReadingTime} minutes</span>
          </div>
        </div>
      </div>
    );
  };

  const renderStyleSelector = () => (
    <div className="mb-6">
      <h4 className="font-semibold text-gray-800 mb-3">TOC Style</h4>
      <div className="grid grid-cols-1 gap-3">
        {TOC_STYLES.map(style => (
          <label key={style.id} className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="tocStyle"
              value={style.id}
              checked={selectedStyle === style.id}
              onChange={(e) => handleStyleChange(e.target.value)}
              className="text-blue-600"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-800">{style.name}</div>
              <div className="text-sm text-gray-600">{style.description}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );

  const renderAdvancedOptions = () => {
    if (!showAdvanced) return null;

    return (
      <div className="mb-6 p-4 border rounded-lg bg-gray-50">
        <h4 className="font-semibold text-gray-800 mb-3">Advanced Options</h4>
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span>Include Word Counts</span>
            <input type="checkbox" className="rounded" />
          </div>
          <div className="flex items-center justify-between">
            <span>Show Subsections</span>
            <input type="checkbox" className="rounded" />
          </div>
          <div className="flex items-center justify-between">
            <span>Use Page Numbers</span>
            <input type="checkbox" className="rounded" />
          </div>
          <div className="flex items-center justify-between">
            <span>Include Empty Sections</span>
            <input type="checkbox" className="rounded" />
          </div>
        </div>
      </div>
    );
  };

  const renderTOCPreview = () => (
    <div className="mb-6">
      <h4 className="font-semibold text-gray-800 mb-3">Preview</h4>
      <div className="bg-white border rounded-lg p-4 max-h-96 overflow-y-auto">
        <pre className="font-mono text-sm whitespace-pre-wrap text-gray-800">
          {previewTOC || 'Generating preview...'}
        </pre>
      </div>
    </div>
  );

  const renderMissingContent = () => {
    if (!enhancedTOC) return null;

    const missingEntries = enhancedTOC.entries.filter(entry => !entry.hasContent && entry.level === 0);
    
    if (missingEntries.length === 0) return null;

    return (
      <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h4 className="font-semibold text-yellow-800 mb-2">Missing Content</h4>
        <p className="text-sm text-yellow-700 mb-2">
          The following sections don't have content yet:
        </p>
        <ul className="text-sm text-yellow-700 list-disc list-inside">
          {missingEntries.map(entry => (
            <li key={entry.id}>{entry.title}</li>
          ))}
        </ul>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">
              Smart Table of Contents Generator
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Automatically generate a professional table of contents from your book structure
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Configuration */}
          <div className="w-1/3 p-6 border-r overflow-y-auto">
            {renderTOCStatistics()}
            {renderStyleSelector()}
            
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-blue-600 hover:text-blue-800 mb-4"
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </button>
            
            {renderAdvancedOptions()}
            {renderMissingContent()}
          </div>

          {/* Right Panel - Preview */}
          <div className="flex-1 p-6 overflow-y-auto">
            {renderTOCPreview()}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {enhancedTOC && (
              <span>
                {enhancedTOC.entries.length} entries • {enhancedTOC.metadata.estimatedReadingTime} est. pages
              </span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={generateTOC}
              disabled={isGenerating}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              {isGenerating ? 'Generating...' : 'Regenerate'}
            </button>
            
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            
            <button
              onClick={handleApplyTOC}
              disabled={!enhancedTOC || isGenerating}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Apply TOC
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableOfContentsGenerator;